// BM-H398 - mirror divergence classification (brief sections 6.6, 6.7, 6.8).
//
// A mirror pair may differ on administrative sections without leaving the
// mirror path: identities, executors (each usually names the other), digital
// assets, funeral wishes and the letter of wishes. A difference on any
// non-administrative section (severance decision, business dispositions,
// guardians, structure, legacies, residue, exclusions) takes the instruction
// off the mirror path: two single wills, MIRROR_SPLIT_MESSAGE shown, and a
// solicitor's call before drafting.
//
// Classification is by section key only. The message never names a section;
// the point of difference is surfaced separately from `divergedSections`.

import { MIRROR_SHORT_KEY, SECTION_KEYS } from "./willsPayload";

export const ADMINISTRATIVE_SECTIONS = Object.freeze([
  "s1_testator",
  "s3_executors",
  "s8_digital",
  "s9_funeral",
  "s11_letter_of_wishes",
]);

export const NON_ADMINISTRATIVE_SECTIONS = Object.freeze([
  "s2_family",
  "s2a_joint_assets",
  "s2b_shares",
  "s4_guardians",
  "s5_structure",
  "s6_legacies",
  "s7_residue",
  "s10_exclusions",
]);

export function classifySection(key) {
  if (ADMINISTRATIVE_SECTIONS.includes(key)) return "administrative";
  if (NON_ADMINISTRATIVE_SECTIONS.includes(key)) return "non_administrative";
  throw new Error(`mirrorDivergence: unknown section ${key}`);
}

function stripBookkeeping(section) {
  if (!section || typeof section !== "object") return section;
  const { not_known, ...rest } = section;
  return rest;
}

export function sectionsDiffer(a, b) {
  return JSON.stringify(stripBookkeeping(a)) !== JSON.stringify(stripBookkeeping(b));
}

// The sections B has answered for themselves (same_as_a === false) whose
// content actually differs from A's.
export function divergedSections(payload) {
  const mirror = payload.submission.mirror || { same_as_a: {}, b_stashed: {} };
  const out = [];
  for (const key of SECTION_KEYS) {
    const short = MIRROR_SHORT_KEY[key];
    if (mirror.same_as_a[short] !== false) continue;
    const b = mirror.b_stashed[key];
    if (!b) continue;
    if (sectionsDiffer(payload.submission.answers[key], b)) out.push(key);
  }
  return out;
}

export function classifyDivergences(payload) {
  const diverged = divergedSections(payload);
  const administrative = diverged.filter((k) => classifySection(k) === "administrative");
  const nonAdministrative = diverged.filter((k) => classifySection(k) === "non_administrative");
  return {
    diverged,
    administrative,
    nonAdministrative,
    splitRequired: nonAdministrative.length > 0,
  };
}

// Section 6.8: A and B naming different first executors is administrative and
// expected (each names the other). Flagged for the fee earner, never a split.
export function firstExecutorDivergence(payload) {
  const mirror = payload.submission.mirror || {};
  const b = mirror.b_stashed?.s3_executors;
  if (!b) return null;
  const aFirst = payload.submission.answers.s3_executors.primary?.[0]?.person_id || null;
  const bFirst = b.primary?.[0]?.person_id || null;
  if (!aFirst || !bFirst || aFirst === bFirst) return null;
  return { a_first_executor: aFirst, b_first_executor: bFirst, classification: "administrative" };
}
